import type { ReactNode } from 'react';

import type { Entry } from '../api/types.ts';
import { useT } from '../i18n/index.ts';
import { Icon, type IconName } from './icons.tsx';

/** Amount or quantity: Western digits, always left-to-right inside Urdu text. */
export function Num({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <span className={`num ${className}`}>{children}</span>;
}

export function OfflinePill({ compact = false }: { compact?: boolean }) {
  const { t } = useT();
  if (compact) {
    return (
      <span
        role="status"
        aria-label={t('app.offline')}
        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-ink text-white"
      >
        <Icon name="cloudOff" size={18} stroke={2.25} />
      </span>
    );
  }
  return (
    <span role="status" className="inline-flex h-8 items-center gap-1.5 rounded-full bg-ink px-2.5 text-[13px] font-bold text-white">
      <Icon name="cloudOff" size={15} stroke={2.25} />
      {t('app.offline')}
    </span>
  );
}

interface ChipLook {
  key: string;
  icon: IconName;
  className: string;
}

/** Status chip: icon + word + tint, readable without colour (docs/DESIGN.md §1.6). */
export function entryChip(e: Entry): ChipLook {
  if (e.syncState === 'queued')
    return { key: 'status.queued', icon: 'cloudOff', className: 'bg-paper text-ink border-[1.5px] border-dashed border-ink' };
  if (e.syncState === 'failed') return { key: 'status.failed', icon: 'alert', className: 'bg-danger-tint text-danger-text' };
  switch (e.status) {
    case 'approved':
      return { key: 'status.approved', icon: 'check', className: 'bg-success-tint text-success-text' };
    case 'rejected':
      return { key: 'status.rejected', icon: 'close', className: 'bg-danger-tint text-danger-text' };
    case 'needs_correction':
      return { key: 'status.needs_correction', icon: 'pencil', className: 'bg-warning-tint text-warning-text' };
    default:
      return { key: 'status.pending', icon: 'clock', className: 'bg-ground text-ink' };
  }
}

export function StatusChip({ entry }: { entry: Entry }) {
  const { t } = useT();
  const look = entryChip(entry);
  return (
    <span className={`inline-flex h-[26px] items-center gap-1 whitespace-nowrap rounded-md px-2 text-xs font-bold ${look.className}`}>
      <Icon name={look.icon} size={13} stroke={2.5} />
      {t(look.key)}
    </span>
  );
}

/** Full-screen loading / error placeholder. */
export function Centered({ children }: { children: ReactNode }) {
  return <div className="flex min-h-app items-center justify-center p-6 text-center text-muted">{children}</div>;
}

/**
 * Animated loading ring. Screen readers hear the label; with "reduce motion" switched on the
 * ring stays still (global rule in styles.css) but remains visible.
 */
export function Spinner({ size = 24, label, className = '' }: { size?: number; label?: string; className?: string }) {
  const { t } = useT();
  return (
    <span role="status" className={`inline-flex shrink-0 items-center justify-center ${className}`}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" className="animate-spin">
        <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="3" opacity="0.2" />
        <path d="M21.5 12A9.5 9.5 0 0 0 12 2.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
      <span className="sr-only">{label ?? t('app.loading')}</span>
    </span>
  );
}

/** Full-screen wait (app start, opening the admin app): the brand with a spinner under it. */
export function PageLoader() {
  return (
    <div className="flex min-h-app flex-col items-center justify-center gap-6 bg-ground p-6">
      <Brand size="lg" />
      <Spinner size={36} className="text-brand" />
    </div>
  );
}

/** The product name (owner's branding, 7 Oct 2026). Kept in Latin letters in both languages. */
export const APP_NAME = 'Kharcha';
/** The Urdu strap line: "an account of every rupee". */
export const TAGLINE_UR = 'ہر روپے کا حساب';

/** The app's logo mark: the receipt icon on a teal tile. */
export function LogoMark({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" aria-hidden="true" className="shrink-0">
      <rect width="512" height="512" rx="112" fill="#0F6E6A" />
      <path d="M150 140h212a24 24 0 0 1 24 24v232l-30-18-30 18-30-18-30 18-30-18-30 18-30-18-30 18V164a24 24 0 0 1 24-24z" fill="#fff" />
      <path d="M196 214h120M196 262h120M196 310h72" stroke="#0F6E6A" strokeWidth="22" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Brand lockup: logo + the "Kharcha" wordmark in teal + a small line under it
 * ("by KCloudAI", or e.g. "Owner dashboard"). md fits headers, lg the loader.
 */
export function Brand({ size = 'md', product }: { size?: 'md' | 'lg'; product?: string }) {
  const { t } = useT();
  const lg = size === 'lg';
  return (
    <span className="inline-flex items-center gap-2.5" dir="ltr">
      <LogoMark size={lg ? 52 : 38} />
      <span className="flex flex-col leading-none">
        <span className={`font-sans font-extrabold tracking-[-0.035em] text-brand ${lg ? 'text-[36px]' : 'text-[24px]'}`}>{APP_NAME}</span>
        <span className={`mt-1 font-sans font-semibold text-muted ${lg ? 'text-sm' : 'text-[12px]'}`}>{product ?? t('app.product')}</span>
      </span>
    </span>
  );
}
